import React from "react";
import { ReferenceLine } from "recharts";
import { parseDate } from "./dateUtils";
import { YAxis } from "recharts";

// Procesamiento de la información del gráfico
export const processChartInfo = (chartsInfo, telemetryDates = []) => {
  const events = chartsInfo.events ?? [];

  const lastTelemetryDate =
    telemetryDates.length > 0
      ? telemetryDates[telemetryDates.length - 1]
      : null;

  // Si no hay telemetría, no se pega ningún evento
  if (!lastTelemetryDate) {
    const eventsMap = events.reduce((acc, event) => {
      acc[`${event.ref}Ref`] = event.date;
      return acc;
    }, {});
    return { adjustedEvents: [], eventsMap, eventTelemetry: [] };
  }

  // Cálculo de next (siguiente evento) para todos los eventos
  const withNext = events.map((event) => ({
    ...event,
    closestDate: findNextDate(event.date, telemetryDates) || null,
  }));

  // Si un evento no tiene telemetría posterior, se sitúa sobre la última telemetría disponible
  const adjustedEvents = withNext.map((event) => {
    if (!event.closestDate) {
      return { ...event, closestDate: lastTelemetryDate };
    }
    return event;
  });

  // Mapeo de eventos
  const eventsMap = events.reduce((acc, event) => {
    acc[`${event.ref}Ref`] = event.date;
    return acc;
  }, {});

  return { adjustedEvents, eventsMap, eventTelemetry: [] };
};

// Búsqueda de fecha posterior más próxima para mostrar eventos en la gráfica
export const findNextDate = (targetDate, availableDates) => {
  const target = new Date(parseDate(targetDate));

  const nextDate = availableDates.reduce((closest, current) => {
    const currentDate = new Date(parseDate(current));
    const closestDate = closest ? new Date(parseDate(closest)) : null;

    if (currentDate >= target && (!closestDate || currentDate < closestDate)) {
      return current; // Actualiza si es la fecha más próxima y válida
    }

    return closest; // Mantiene la fecha más próxima encontrada
  }, null);

  return nextDate || null; // Devuelve null si no hay fecha válida
};

// Actualización de telemetrías para incluir información de eventos
export const updateTelemetryInfo = (
  telemetryInfo,
  adjustedEvents,
  eventTelemetry,
) => {
  const hasInReturn = adjustedEvents.some((event) => event.ref === "inReturn");

  // Filtro de eventos que deben desaparecer si hay inReturn
  const filteredEvents = adjustedEvents.filter((event) => {
    if (hasInReturn && event.ref === "delivered") {
      return false;
    }
    return true;
  });

  // 1) Agrupar eventos por closestDate
  //    clave = fecha de telemetría a la que se pegan
  const eventsByTelemetryDate = filteredEvents.reduce((acc, event) => {
    const key = event.closestDate;
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(event);
    return acc;
  }, {});

  // 2) Mapear telemetrías añadiendo TODOS los eventos que caen en esa fecha
  const updatedTelemetry = telemetryInfo.map((telemetry) => {
    const eventsForThisTelemetry = eventsByTelemetryDate[telemetry.date];

    if (eventsForThisTelemetry && eventsForThisTelemetry.length > 0) {
      // ordenamos por fecha para saber cuál es el "último"
      const sorted = [...eventsForThisTelemetry].sort(
        (a, b) => new Date(a.date) - new Date(b.date),
      );
      const lastEvent = sorted[sorted.length - 1];

      return {
        ...telemetry,
        isEvent: true,
        name: lastEvent.name,
        ref: lastEvent.ref,
        eventDate: lastEvent.date,
        events: sorted.map((event) => ({
          name: event.name,
          ref: event.ref,
          eventDate: event.date,
        })),
      };
    }

    return telemetry;
  });

  // 3) Eventos sin closestDate
  const filteredEventTelemetry = eventTelemetry.filter((event) => {
    if (hasInReturn && event.ref === "delivered") {
      return false;
    }
    return true;
  });

  const eventTelemetryWithArrays = filteredEventTelemetry.map((event) => ({
    ...event,
    events: [
      {
        name: event.name,
        ref: event.ref,
        eventDate: event.eventDate || event.date,
      },
    ],
  }));

  return [...updatedTelemetry, ...eventTelemetryWithArrays];
};

export function renderHumidityYAxis(isStandalone) {
  return !isStandalone ? (
    <YAxis
      yAxisId="right"
      label={{ value: "H. (%)", position: "top", offset: 25 }}
      orientation="right"
      unit="%"
      domain={[0, 100]}
    />
  ) : (
    <YAxis yAxisId="right" orientation="right" />
  );
}

// Renderizado de líneas de referencia para eventos
export const renderReferenceLines = (chartParameters, showEvents) => {
  if (!showEvents) return null;

  const hasInReturnAndDelivered =
    chartParameters.telemetryInfo.some(
      (data) => data.isEvent && data.ref === "inReturn",
    ) &&
    chartParameters.telemetryInfo.some(
      (data) => data.isEvent && data.ref === "delivered",
    );

  return chartParameters.telemetryInfo
    .filter((data) => {
      if (!data.isEvent) return false;

      // si hay inReturn y delivered, se oculta delivered
      if (hasInReturnAndDelivered && data.ref === "delivered") {
        return false;
      }

      return true;
    })
    .map((data, index) => (
      <ReferenceLine
        key={`event-${index}`}
        x={data.date}
        stroke="#82ca9d"
        yAxisId="left"
        label={{
          value: data.name,
          position: "insideTopRight",
          fill: "#274227",
          angle: -90,
          fontSize: 14,
          offset: 15,
        }}
      />
    ));
};

// Verificación de datos de humedad disponibles
export const isHumidityDisabled = (telemetryInfo) => {
  return !telemetryInfo.some((data) => data.humidity !== undefined);
};

// Verificación de límites de temperatura definidos
export const isLimitsDisabled = (tempMax, tempMin) => {
  return !tempMax && !tempMin;
};

export const isEventDisabled = (events) => {
  return events.length === 0;
};

// Funciones para obtener el dominio máximo y mínimo de la gráfica
export const getMaxDomainValue = (chartParameters) => {
  const { tempMax, tempMin, maxRecord } = chartParameters;

  if (tempMax === null && tempMin !== null && tempMin > maxRecord) {
    return Math.ceil(tempMin + 2);
  }

  if (tempMax === null || maxRecord > tempMax) {
    return Math.ceil(maxRecord + 2);
  }

  return Math.ceil(tempMax + 2);
};

export const getMinDomainValue = (chartParameters) => {
  const { tempMax, tempMin, minRecord } = chartParameters;

  if (tempMin == null && tempMax !== null && tempMax < minRecord) {
    return Math.floor(tempMax - 2);
  }

  if (tempMin === null || minRecord < tempMin) {
    return Math.floor(minRecord - 2);
  }

  return Math.floor(tempMin - 2);
};

// Función para visualización dinámica de datos en la gráfica
export const handleToggle = (toggleKey, showChart, setToggleStates) => {
  const newState = { ...showChart };

  switch (toggleKey) {
    case "showTemperature":
      if (showChart.showTemperature) {
        newState.showTemperature = false;
        newState.showAlerts = false;
        newState.showLimits = false;
      } else {
        newState.showTemperature = true;
      }
      break;

    case "showAlerts":
      if (!showChart.showTemperature) {
        newState.showTemperature = true;
        newState.showAlerts = true;
      } else {
        newState.showAlerts = !showChart.showAlerts;
      }
      break;

    case "showLimits":
      if (!showChart.showTemperature) {
        newState.showTemperature = true;
        newState.showLimits = true;
      } else {
        newState.showLimits = !showChart.showLimits;
      }
      break;

    case "showEvents":
      newState.showEvents = !showChart.showEvents;
      break;

    case "showHumidity":
      newState.showHumidity = !showChart.showHumidity;
      break;

    default:
      break;
  }

  setToggleStates(newState);
};

// Función para obtener las configuraciones de visualización de la gráfica
export const getChartViewSettings = (variant) => {
  switch (variant) {
    case "full":
      return {
        showTemperature: true,
        showHumidity: true,
        showLimits: true,
        showEvents: true,
        showAlerts: true,
      };
    case "temperature":
      return {
        showTemperature: true,
        showHumidity: false,
        showLimits: true,
        showEvents: true,
        showAlerts: false,
      };
    case "humidity":
      return {
        showTemperature: false,
        showHumidity: true,
        showLimits: false,
        showEvents: true,
        showAlerts: false,
      };
    default:
      return {};
  }
};

// Función para construir el resumen de alertas de los informes
export const buildAlertSummary = (alertsList, chartsInfo, variant = "full") => {
  const telemetry = chartsInfo?.telemetryInfo?.[variant] ?? [];

  return (alertsList ?? [])
    .filter((alert) => alert.class === "alert")
    .map((alert) => {
      const matchingTelemetry = telemetry.find((t) => t.date === alert.date);
      return {
        ...alert,
        value: matchingTelemetry ? matchingTelemetry.temp : null,
      };
    });
};
